import { useCallback, useContext, useMemo } from 'react';
import { SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { SmartSendContext } from '../../../lib/context/SmartSendContext';
import SmartSendItemInactive from './SmartSendItemInactive';
import SmartSendItemActive from './SmartSendItemActive';
import {
  ArrangeReturnCollectionAction,
  EditItemStatusAction,
  ReleaseItemsAction,
  RemoveTagAction,
  SaveItemsAction,
  SaveTagsAction,
  UpdateItemCacheAction
} from '../../../lib/context/SmartSendContextModels';
import SmartSendItemBooked from './SmartSendItemBooked';
import SmartSendItemProcessing from './SmartSendItemProcessing';
import SetMarketplaceAction, { ItemActionType } from './SetMarketplaceAction';
import SmartSendOrderProcessOrder from './SmartSendOrderProcessOrder';
import { IsReturnCollection, IsSmartSendByType } from '../../../lib/helpers/SmartSendSummaryGenerator';
import { EditType } from './EditType';
import SmartSendItemActiveMobile from './SmartSendItemActiveMobile';
import SmartSendItemBookedMobile from './SmartSendItemBookedMobile';
import SmartSendOrderProcessOrderMobile from './SmartSendOrderProcessOrderMobile';
import SmartSendItemInactiveMobile from './SmartSendItemInactiveMobile';
import SmartSendItemProcessingMobile from './SmartSendItemProcessingMobile';
import { useEventEmitter } from '../../../lib/em/Hooks';
import { Consts, EditEventType } from '../../../lib/em/EmitTypes';
import { EventSections } from '../../../lib/events/EventLogger';
import { CanArrangeReturn } from '../../../lib/helpers/CanArrangeReturn';
import useGoto from '~/hooks/use-goto';
import { RoutingConstants } from '~/apps/smartsend/lib/consts/RoutingConstants';
import { GenerateBaseAnalyticsPayload } from '~/lib/utils/analytics';
import useSmartSendAnalytics from '~/hooks/use-smartsend-analytics';
import { ToSmartSendAnalyticsModel } from '../quotes/utils/CurrentSelectionType';
import { useNavExperience } from '~/components/nav/nav-experience';
import useOrderProcessLinks from '~/hooks/use-order-process-links';

export interface SmartSendItemProps {
  item: SmartSendItemApiModel;
  isXs: boolean;
}

export default function SmartSendItemDisplay(props: SmartSendItemProps) {
  const { dispatch } = useContext(SmartSendContext);
  const { emit } = useEventEmitter<EditEventType>();
  const { goTo } = useGoto();
  const { track } = useSmartSendAnalytics();
  const experience = useNavExperience();
  const { goToReturn } = useOrderProcessLinks();

  const display = useMemo(() => {
    const isXs = props.isXs ? 'Mobile' : 'Desktop';
    if (!IsSmartSendByType(props.item.Type)) {
      return `NonSmartSend_${isXs}`;
    }
    if (props.item.Status === 'Inactive') {
      return `Inactive_${isXs}`;
    }
    if (['AwaitingDispatch', 'Dispatched'].includes(props.item.MarketplaceStatus)) {
      return `Booked_${isXs}`;
    }
    if (props.item.BookingDetails?.BookingStatus === 'AwaitingPayment') {
      return `AwaitingPayment_${isXs}`;
    }
    return `Active_${isXs}`;
  }, [
    props.item.Type,
    props.item.Status,
    props.item.MarketplaceStatus,
    props.item.BookingDetails?.BookingStatus,
    props.isXs
  ]);

  const isReturn = IsReturnCollection(props.item);
  const canReturn = !isReturn && CanArrangeReturn(props.item);

  const logEvent = useCallback(
    (events: string[]) => {
      if (display.startsWith('Active_') || display.startsWith('AwaitingPayment_')) {
        track({
          type: 'quick_actions_click',
          payload: {
            ...GenerateBaseAnalyticsPayload([EventSections.SmartSend, EventSections.SmartSendItem, ...events]),
            action: events?.join(','),
            items: [ToSmartSendAnalyticsModel(props.item)]
          }
        });
      } else {
        track({
          type: 'quick_actions_click',
          payload: {
            ...GenerateBaseAnalyticsPayload([EventSections.BookedItems, EventSections.SmartSendItem]),
            action: events?.join(','),
            items: [ToSmartSendAnalyticsModel(props.item)]
          }
        });
      }
    },
    [display, track, props.item]
  );

  const toggleSelect = useCallback(
    (val: boolean) => {
      dispatch(new UpdateItemCacheAction(props.item.Id, { ...props.item.State, selected: val }));
    },
    [dispatch, props.item.Id, props.item.State]
  );

  const removeTag = useCallback(
    (tag: string) => {
      logEvent(['remove-tags']);
      dispatch(new RemoveTagAction(props.item.Id, tag));
    },
    [dispatch, props.item.Id, logEvent]
  );

  const saveTags = useCallback(
    (tags: string[]) => {
      logEvent(['save-tags']);
      dispatch(new SaveTagsAction([props.item.Id], tags));
    },
    [dispatch, props.item.Id, logEvent]
  );

  const setEdit = useCallback(
    (type: EditType) => {
      logEvent([type?.toLowerCase()]);
      emit(Consts.Edit, { Id: props.item.Id, Action: type });
    },
    [emit, logEvent, props.item.Id]
  );

  const setAction = useCallback(
    (type: ItemActionType) => {
      logEvent([type?.toLowerCase()]);
      SetMarketplaceAction(dispatch, props.item.Id, type);
    },
    [dispatch, props.item.Id, logEvent]
  );

  const changeStatus = useCallback(
    (status: string, select: boolean) => {
      logEvent([`status`, status]);
      dispatch(new EditItemStatusAction(props.item.Id, status, select));
    },
    [dispatch, props.item.Id, logEvent]
  );

  const restore = useCallback(
    (select: boolean) => {
      changeStatus('Active', select);
    },
    [changeStatus]
  );

  const saveForLater = useCallback(
    (select: boolean) => {
      changeStatus('Inactive', select);
    },
    [changeStatus]
  );

  const deleteItem = useCallback(() => {
    changeStatus('Deleted', false);
  }, [changeStatus]);

  const arrangeReturn = useCallback(async () => {
    // Consumer experience: hand off to the order-process (checkout) return flow by order line id
    // instead of creating a return order in SmartSend. If the order line id is missing, fall through
    // to the existing SmartSend behaviour rather than no-op.
    if (experience === 'consumer' && props.item.BookingDetails?.OrderLineId) {
      goToReturn(props.item.BookingDetails.OrderLineId);
      return;
    }
    dispatch(new ArrangeReturnCollectionAction([props.item.Id]));
    goTo(RoutingConstants.SmartSend);
  }, [dispatch, props.item.Id, props.item.BookingDetails?.OrderLineId, goTo, experience, goToReturn]);

  const setDate = useCallback(
    async (d: Date) => {
      logEvent(['set-date']);
      dispatch(new SaveItemsAction([props.item.Id], ['PreferredCollectionDate'], { PreferredCollectionDate: d }));
    },
    [dispatch, props.item.Id, logEvent]
  );

  const release = useCallback(() => {
    dispatch(new ReleaseItemsAction([props.item.Id]));
  }, [dispatch, props.item.Id]);

  const book = useCallback(() => {
    emit(Consts.Book, { Open: true, Ids: [props.item.Id] });
  }, [emit, props.item.Id]);

  switch (display) {
    case 'NonSmartSend_Mobile':
      return (
        <SmartSendOrderProcessOrderMobile
          key={props.item.Id}
          item={props.item}
          toggleSelect={toggleSelect}
          edit={setEdit}
          isReturn={isReturn}
        />
      );
    case 'NonSmartSend_Desktop':
      return (
        <SmartSendOrderProcessOrder
          key={props.item.Id}
          item={props.item}
          toggleSelect={toggleSelect}
          edit={setEdit}
          isReturn={isReturn}
          arrangeReturn={canReturn ? arrangeReturn : null}
        />
      );
    case 'Inactive_Mobile':
      return (
        <SmartSendItemInactiveMobile
          key={props.item.Id}
          removeTag={removeTag}
          saveTags={saveTags}
          item={props.item}
          toggleSelect={toggleSelect}
          restore={restore}
          deleteItem={deleteItem}
          isReturn={isReturn}
        />
      );
    case 'Inactive_Desktop':
      return (
        <SmartSendItemInactive
          key={props.item.Id}
          removeTag={removeTag}
          saveTags={saveTags}
          item={props.item}
          toggleSelect={toggleSelect}
          restore={restore}
          delete={deleteItem}
          isReturn={isReturn}
        />
      );
    case 'Booked_Mobile':
      return (
        <SmartSendItemBookedMobile
          key={props.item.Id}
          item={props.item}
          toggleSelect={toggleSelect}
          edit={setEdit}
          removeTag={removeTag}
          saveTags={saveTags}
          action={setAction}
          isReturn={isReturn}
        />
      );
    case 'Booked_Desktop':
      return (
        <SmartSendItemBooked
          key={props.item.Id}
          item={props.item}
          toggleSelect={toggleSelect}
          edit={setEdit}
          removeTag={removeTag}
          saveTags={saveTags}
          action={setAction}
          isReturn={isReturn}
          arrangeReturn={canReturn ? arrangeReturn : null}
        />
      );
    case 'AwaitingPayment_Mobile':
      return (
        <SmartSendItemProcessingMobile key={props.item.Id} item={props.item} cancel={release} isReturn={isReturn} />
      );
    case 'AwaitingPayment_Desktop':
      return <SmartSendItemProcessing key={props.item.Id} item={props.item} cancel={release} isReturn={isReturn} />;
    case 'Active_Mobile':
      return (
        <SmartSendItemActiveMobile
          key={props.item.Id}
          item={props.item}
          toggleSelect={toggleSelect}
          edit={setEdit}
          removeTag={removeTag}
          saveTags={saveTags}
          action={setAction}
          isReturn={isReturn}
          saveForLater={saveForLater}
          deleteItem={deleteItem}
          book={book}
        />
      );
    case 'Active_Desktop':
      return (
        <SmartSendItemActive
          key={props.item.Id}
          item={props.item}
          setDate={setDate}
          toggleSelect={toggleSelect}
          edit={setEdit}
          removeTag={removeTag}
          saveTags={saveTags}
          action={setAction}
          saveForLater={saveForLater}
          deleteItem={deleteItem}
          book={book}
          isReturn={isReturn}
        />
      );
    default:
      return null;
  }
}
